// ============================================
// Path: /frontend/src/pages/admin/SettingsPage.jsx
// Purpose: Admin page for managing system settings
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { adminAPI } from '../../api/admin.api';
import { useToast } from '../../hooks/useToast';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';

// ========================================
// LOADING SKELETON COMPONENT
// ========================================
const LoadingSkeleton = () => (
  <>
    <div className="space-y-4">
      <Skeleton className="h-6 w-1/4" />
      <Skeleton className="h-10 w-full" />
    </div>
    <div className="space-y-4">
      <Skeleton className="h-6 w-1/4" />
      <Skeleton className="h-10 w-full" />
    </div>
    <div className="flex items-center space-x-4">
      <Skeleton className="h-8 w-1/4" />
      <Skeleton className="h-6 w-12 rounded-full" />
    </div>
  </>
);

// ========================================
// MAIN COMPONENT
// ========================================
export default function SettingsPage() {
  console.log('🎨 [SettingsPage] Component mounted');

  const { toast } = useToast();

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchSettings = async () => {
      console.log('🔵 [SettingsPage] Fetching settings...');
      setLoading(true);

      try {
        const data = await adminAPI.getSettings();
        console.log('✅ [SettingsPage] Settings received:', data);
        setSettings(data.settings);
      } catch (err) {
        console.error('❌ [SettingsPage] Fetch error:', err);
        setSettings({
          siteName: 'MotoHome Finder',
          supportEmail: 'support@example.com',
          maintenanceMode: false,
          allowNewRegistrations: true,
          listingExpirationDays: 30,
          currency: 'KES',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleSave = async () => {
    if (!settings) return;

    console.log('🔵 [SettingsPage] Saving settings...', settings);
    setIsSaving(true);

    try {
      await adminAPI.updateSettings(settings);
      console.log('✅ [SettingsPage] Settings saved successfully');
      toast({
        title: 'Settings Saved',
        description: 'Your changes have been successfully saved.',
      });
    } catch (err) {
      console.error('❌ [SettingsPage] Save error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not save settings. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e) => {
    if (!settings) return;
    const { id, value } = e.target;
    console.log('🔄 [SettingsPage] Input changed:', { id, value });
    setSettings({ ...settings, [id]: value });
  };

  const handleSwitchChange = (id, checked) => {
    if (!settings) return;
    console.log('🔄 [SettingsPage] Switch changed:', { id, checked });
    setSettings({ ...settings, [id]: checked });
  };

  const handleSelectChange = (id, value) => {
    if (!settings) return;
    console.log('🔄 [SettingsPage] Select changed:', { id, value });
    setSettings({ ...settings, [id]: value });
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <Card>
      <CardHeader>
        <CardTitle>System Settings</CardTitle>
        <CardDescription>
          Configure general site settings, registration, and monetization.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {loading || !settings ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* General Settings */}
            <div>
              <h3 className="text-lg font-medium mb-4 border-b pb-2">General</h3>
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 items-center gap-4">
                  <Label htmlFor="siteName" className="md:text-right">
                    Site Name
                  </Label>
                  <Input
                    id="siteName"
                    value={settings.siteName}
                    onChange={handleInputChange}
                    className="col-span-2"
                  />
                </div>
                <div className="grid md:grid-cols-3 items-center gap-4">
                  <Label htmlFor="supportEmail" className="md:text-right">
                    Support Email
                  </Label>
                  <Input
                    id="supportEmail"
                    type="email"
                    value={settings.supportEmail}
                    onChange={handleInputChange}
                    className="col-span-2"
                  />
                </div>
                <div className="grid md:grid-cols-3 items-center gap-4">
                  <Label htmlFor="maintenanceMode" className="md:text-right">
                    Maintenance Mode
                  </Label>
                  <div className="col-span-2">
                    <Switch
                      id="maintenanceMode"
                      checked={settings.maintenanceMode}
                      onCheckedChange={(c) => handleSwitchChange('maintenanceMode', c)}
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Temporarily disable access to the public-facing site.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Registration Settings */}
            <div>
              <h3 className="text-lg font-medium mb-4 border-b pb-2">Registration</h3>
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 items-center gap-4">
                  <Label htmlFor="allowNewRegistrations" className="md:text-right">
                    Enable New Registrations
                  </Label>
                  <div className="col-span-2">
                    <Switch
                      id="allowNewRegistrations"
                      checked={settings.allowNewRegistrations}
                      onCheckedChange={(c) => handleSwitchChange('allowNewRegistrations', c)}
                    />
                    <p className="text-sm text-muted-foreground mt-1">Allow new users to create accounts.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Monetization Settings */}
            <div>
              <h3 className="text-lg font-medium mb-4 border-b pb-2">Monetization & Listings</h3>
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 items-center gap-4">
                  <Label htmlFor="listingExpirationDays" className="md:text-right">
                    Listing Expiration (Days)
                  </Label>
                  <Input
                    id="listingExpirationDays"
                    type="number"
                    value={settings.listingExpirationDays}
                    onChange={handleInputChange}
                    className="col-span-2"
                  />
                </div>
                <div className="grid md:grid-cols-3 items-center gap-4">
                  <Label htmlFor="currency" className="md:text-right">
                    Currency
                  </Label>
                  <Select
                    onValueChange={(v) => handleSelectChange('currency', v)}
                    value={settings.currency}
                  >
                    <SelectTrigger id="currency" className="col-span-2">
                      <SelectValue placeholder="Select a currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KES">KES (Kenyan Shilling)</SelectItem>
                      <SelectItem value="USD">USD (US Dollar)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
      <CardFooter className="border-t pt-6">
        <Button onClick={handleSave} disabled={isSaving || loading}>
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Settings
        </Button>
      </CardFooter>
    </Card>
  );
}